import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { UserBadge } from '../components/UserBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ShoppingCart, Plus, Search, Loader2, Trash2, ExternalLink, X, ChevronDown, ChevronUp, Package } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  draft:      { label: 'Bozza',          color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
  confirmed:  { label: 'Confermato',     color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  processing: { label: 'In lavorazione', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  shipped:    { label: 'Spedito',        color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  delivered:  { label: 'Consegnato',     color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  cancelled:  { label: 'Annullato',      color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

const VAT_RATES = [0, 4, 10, 22];

const emptyItem = () => ({
  _rid: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  product_code: '',
  description: '',
  lot_number: '',
  unit_measure: 'NR',
  quantita_per_collo: 1,   // from product (auto-filled, read-only)
  num_colli: 1,             // user enters this
  quantity: 1,              // = num_colli × quantita_per_collo (auto)
  peso_lordo_unit: 0,       // g per unit (from product, read-only)
  volume_unit: 0,           // m³ per unit (from product, read-only)
  unit_price: 0,
  discount: 0,
  vat_rate: 4,
  // ── Packaging (catalog reference) ────────────────────────────────────
  packaging_id: '',
  packaging_name: '',
  packaging_material: '',
  packaging_length_cm: 0,
  packaging_width_cm: 0,
  packaging_height_cm: 0,
  packaging_volume_m3: 0,    // unit volume from catalog (m³ per box)
  packaging_tare_g: 0,
});

const initForm = () => ({
  company_id: '', contact_id: '', opportunity_id: '', assigned_to: '',
  expected_delivery: '', notes: '',
  destinazione_nome: '', destinazione_indirizzo: '', destinazione_cap_citta: '',
  aspetto_beni: 'Cassa cartone e cassette di plastica', causale_trasporto: 'Vendita',
  tipo_movimentazione: '',
  trasporto_a_cura_di: '', data_ora_trasporto: '', vettore: '',
  data_ora_ritiro: '', num_colli_totale: 0, peso_lordo: '', volume: '',
  riferimento: '', cod_cliente: '', note_ddt: '',
});

const formatEuro = (v) => `€ ${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Orders() {
  const navigate = useNavigate();
  const { canCreate, canDelete } = usePermissions();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initForm());
  const [items, setItems] = useState([emptyItem()]);
  const [showDDTFields, setShowDDTFields] = useState(false);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [pickerItemIdx, setPickerItemIdx] = useState(null);
  const [packagings, setPackagings] = useState([]);
  const [editId, setEditId] = useState(null);  // when set, form is in EDIT mode
  const [editOrderNum, setEditOrderNum] = useState('');

  const fetchAll = async () => {
    try {
      const [ordersRes, statsRes, compRes, usersRes, prodRes, pkgRes] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/orders/stats`),
        axios.get(`${API}/companies`),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/products`).catch(() => ({ data: [] })),
        axios.get(`${API}/packaging`).catch(() => ({ data: [] })),
      ]);
      setOrders(ordersRes.data);
      setStats(statsRes.data);
      setCompanies(compRes.data);
      setUsers(usersRes.data);
      setProducts(prodRes.data);
      setPackagings(pkgRes.data);
    } catch { toast.error('Errore caricamento'); } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  // Handle query param auto-open (e.g. from CompanyDetail)
  useEffect(() => {
    const companyId = searchParams.get('company_id');
    const openNew   = searchParams.get('new');
    const editParam = searchParams.get('edit');
    if (companyId && openNew === '1') {
      setForm(f => ({ ...f, company_id: companyId }));
      setShowForm(true);
    }
    if (editParam) {
      // Load order for editing
      axios.get(`${API}/orders/${editParam}`).then(r => {
        const o = r.data;
        if (o.status !== 'draft') {
          toast.error('Solo gli ordini in bozza possono essere modificati');
          navigate(`/orders/${editParam}`);
          return;
        }
        setEditId(editParam);
        setEditOrderNum(o.order_number || '');
        setForm({
          company_id: o.company_id || '',
          contact_id: o.contact_id || '',
          opportunity_id: o.opportunity_id || '',
          assigned_to: o.assigned_to || '',
          expected_delivery: o.expected_delivery || '',
          notes: o.notes || '',
          destinazione_nome: o.destinazione_nome || '',
          destinazione_indirizzo: o.destinazione_indirizzo || '',
          destinazione_cap_citta: o.destinazione_cap_citta || '',
          aspetto_beni: o.aspetto_beni || 'Cassa cartone e cassette di plastica',
          causale_trasporto: o.causale_trasporto || 'Vendita',
          tipo_movimentazione: o.tipo_movimentazione || '',
          trasporto_a_cura_di: o.trasporto_a_cura_di || '',
          data_ora_trasporto: o.data_ora_trasporto || '',
          vettore: o.vettore || '',
          data_ora_ritiro: o.data_ora_ritiro || '',
          num_colli_totale: o.num_colli_totale || 0,
          peso_lordo: o.peso_lordo || '',
          volume: o.volume || '',
          riferimento: o.riferimento || '',
          cod_cliente: o.cod_cliente || '',
          note_ddt: o.note_ddt || '',
        });
        const loaded = (o.items || []).map(it => ({
          ...emptyItem(),
          ...it,
          _rid: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
        }));
        setItems(loaded.length ? loaded : [emptyItem()]);
        setShowForm(true);
        if (o.destinazione_nome || o.cod_cliente) setShowDDTFields(true);
      }).catch(() => {
        toast.error('Ordine non trovato');
        navigate('/orders');
      });
    }
  }, [searchParams]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (form.company_id) {
      axios.get(`${API}/contacts`, { params: { company_id: form.company_id } })
        .then(r => setContacts(r.data)).catch(() => {});
      // Pre-fill DDT fields from company data
      const comp = companies.find(c => (c._id || c.id) === form.company_id);
      if (comp) {
        setForm(f => ({
          ...f,
          cod_cliente: comp.codice_cliente || '',
          destinazione_nome: f.destinazione_nome || comp.ddt_destinazione_nome || '',
          destinazione_indirizzo: f.destinazione_indirizzo || comp.ddt_destinazione_indirizzo || '',
          destinazione_cap_citta: f.destinazione_cap_citta || comp.ddt_destinazione_cap_citta || '',
          causale_trasporto: f.causale_trasporto !== 'Vendita' ? f.causale_trasporto : (comp.ddt_causale_trasporto || 'Vendita'),
          aspetto_beni: f.aspetto_beni !== 'Cassa cartone e cassette di plastica' ? f.aspetto_beni : (comp.ddt_aspetto_beni || 'Cassa cartone e cassette di plastica'),
          trasporto_a_cura_di: f.trasporto_a_cura_di || comp.ddt_trasporto_a_cura_di || '',
          vettore: f.vettore || comp.ddt_vettore || '',
        }));
        // Auto-expand DDT section if company has DDT data
        if (comp.ddt_destinazione_nome || comp.codice_cliente) setShowDDTFields(true);
      }
    }
  }, [form.company_id, companies]);

  const addItem    = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const changeItem = (idx, field, value) =>
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const updated = { ...it, [field]: value };
      // Recalculate quantity when num_colli or quantita_per_collo changes
      if (field === 'num_colli' || field === 'quantita_per_collo') {
        const nc  = parseFloat(field === 'num_colli' ? value : it.num_colli) || 0;
        const qpc = parseFloat(field === 'quantita_per_collo' ? value : it.quantita_per_collo) || 1;
        updated.quantity = nc * qpc;
      }
      return updated;
    }));

  const openProductPicker = (idx) => { setPickerItemIdx(idx); setProductSearch(''); setShowProductPicker(true); };
  const selectProduct = (prod) => {
    if (pickerItemIdx === null) return;
    const qpc = parseFloat(prod.quantita_per_collo ?? prod.quantity_default ?? 1) || 1;
    const nc  = parseInt(prod.colli_default ?? 1) || 1;
    // If product has a default packaging, look it up in the catalog and apply it
    const defaultPkg = prod.packaging_id
      ? packagings.find(p => (p._id || p.id) === prod.packaging_id)
      : null;
    setItems(prev => prev.map((it, i) => i === pickerItemIdx ? {
      ...it,
      product_code: prod.product_code || '',
      description: prod.description || '',
      lot_number: prod.lot_number || '',
      unit_measure: prod.unit_measure || 'NR',
      quantita_per_collo: qpc,
      num_colli: nc,
      quantity: nc * qpc,
      peso_lordo_unit: typeof prod.peso_lordo === 'number' ? prod.peso_lordo : (parseFloat(prod.peso_lordo) || 0),
      volume_unit: typeof prod.volume === 'number' ? prod.volume : (parseFloat(prod.volume) || 0),
      unit_price: prod.unit_price ?? 0,
      vat_rate: prod.vat_rate ?? 4,
      // Auto-load default packaging from product
      packaging_id: defaultPkg ? (defaultPkg._id || defaultPkg.id) : '',
      packaging_name: defaultPkg ? defaultPkg.name : '',
      packaging_material: defaultPkg ? (defaultPkg.material_type || '') : '',
      packaging_length_cm: defaultPkg ? (parseFloat(defaultPkg.length_cm) || 0) : 0,
      packaging_width_cm:  defaultPkg ? (parseFloat(defaultPkg.width_cm)  || 0) : 0,
      packaging_height_cm: defaultPkg ? (parseFloat(defaultPkg.height_cm) || 0) : 0,
      packaging_volume_m3: defaultPkg ? (parseFloat(defaultPkg.volume_m3) || 0) : 0,
      packaging_tare_g:    defaultPkg ? (parseFloat(defaultPkg.tare_weight_g) || 0) : 0,
    } : it));
    setShowProductPicker(false);
    if (defaultPkg) {
      toast.success(`Articolo + imballaggio "${defaultPkg.name}" caricati`);
    }
  };
  const filteredProducts = products.filter(p =>
    !productSearch || [p.product_code, p.description, p.lot_number]
      .filter(Boolean).some(v => v.toLowerCase().includes(productSearch.toLowerCase()))
  );

  // ── Set packaging on a row (clear or apply) ────────────────────────────────
  const setPackaging = (idx, pkgId) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      if (!pkgId || pkgId === 'none') {
        return {
          ...it,
          packaging_id: '', packaging_name: '', packaging_material: '',
          packaging_length_cm: 0, packaging_width_cm: 0, packaging_height_cm: 0,
          packaging_volume_m3: 0, packaging_tare_g: 0,
        };
      }
      const pkg = packagings.find(p => (p._id || p.id) === pkgId);
      if (!pkg) return it;
      return {
        ...it,
        packaging_id: pkg._id || pkg.id,
        packaging_name: pkg.name || '',
        packaging_material: pkg.material_type || '',
        packaging_length_cm: parseFloat(pkg.length_cm) || 0,
        packaging_width_cm:  parseFloat(pkg.width_cm)  || 0,
        packaging_height_cm: parseFloat(pkg.height_cm) || 0,
        packaging_volume_m3: parseFloat(pkg.volume_m3) || 0,
        packaging_tare_g: parseFloat(pkg.tare_weight_g) || 0,
      };
    }));
  };

  // ── Per-item computed values ───────────────────────────────────────────────
  // When packaging is selected → row volume = num_colli × packaging volume (override).
  // Otherwise → row volume = quantity × volume_unit (from product).
  const itemPeso = (it) => {
    const base = it.quantity * (it.peso_lordo_unit || 0);
    const tara = (parseInt(it.num_colli) || 0) * (it.packaging_tare_g || 0);
    return base + tara;
  };
  const itemVol = (it) => {
    if (it.packaging_id && it.packaging_volume_m3) {
      return (parseInt(it.num_colli) || 0) * it.packaging_volume_m3;
    }
    return it.quantity * (it.volume_unit || 0);
  };
  const fmtQ = (v, d = 3) => v === 0 ? '0' : Number(v).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: d });

  const subtotal  = items.reduce((s, it) => s + it.quantity * it.unit_price * (1 - it.discount / 100), 0);
  const taxAmount = items.reduce((s, it) => s + it.quantity * it.unit_price * (1 - it.discount / 100) * it.vat_rate / 100, 0);
  const total     = subtotal + taxAmount;

  // ── Auto-calculated DDT totals ─────────────────────────────────────────────
  const autoColli = items.reduce((s, it) => s + (parseInt(it.num_colli) || 0), 0);
  const autoPeso  = items.reduce((s, it) => s + itemPeso(it), 0);
  const autoVol   = items.reduce((s, it) => s + itemVol(it), 0);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.company_id) { toast.error('Seleziona un cliente'); return; }
    if (items.every(it => !it.description)) { toast.error('Aggiungi almeno un articolo'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        // Build items with calculated peso_lordo and volume (totali per riga)
        items: items.filter(it => it.description).map(({ _rid, ...it }) => ({
          ...it,
          peso_lordo: itemPeso(it),
          volume: itemVol(it),
        })),
        num_colli_totale: autoColli,
        peso_lordo: autoPeso,
        volume: autoVol,
      };
      if (!payload.contact_id)    delete payload.contact_id;
      if (!payload.assigned_to)   delete payload.assigned_to;
      if (!payload.expected_delivery) delete payload.expected_delivery;
      if (!payload.opportunity_id) delete payload.opportunity_id;
      if (payload.tipo_movimentazione === 'none') payload.tipo_movimentazione = '';
      if (editId) {
        await axios.put(`${API}/orders/${editId}`, payload);
        toast.success(`Ordine ${editOrderNum} aggiornato`);
        setShowForm(false);
        setForm(initForm());
        setItems([emptyItem()]);
        setShowDDTFields(false);
        const idToOpen = editId;
        setEditId(null);
        setEditOrderNum('');
        navigate(`/orders/${idToOpen}`);
      } else {
        const res = await axios.post(`${API}/orders`, payload);
        toast.success(`Ordine ${res.data.order_number} creato`);
        setShowForm(false);
        setForm(initForm());
        setItems([emptyItem()]);
        setShowDDTFields(false);
        fetchAll();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
  };

  const handleDelete = async (id, num) => {
    if (!window.confirm(`Eliminare ordine ${num}?`)) return;
    try {
      await axios.delete(`${API}/orders/${id}`);
      toast.success('Ordine eliminato');
      fetchAll();
    } catch { toast.error('Errore'); }
  };

  const filtered = orders.filter(o => {
    const matchSearch = !search || [o.order_number, o.company_name, o.assigned_name]
      .filter(Boolean).some(f => f.toLowerCase().includes(search.toLowerCase()));
    const matchStatus = filterStatus === 'all' || o.status === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Ordini</h1>
          <p className="text-muted-foreground text-sm mt-1">{orders.length} ordini totali</p>
        </div>
        {canCreate('orders') && (
          <Button onClick={() => setShowForm(true)} data-testid="add-order-btn">
            <Plus size={16} className="mr-2" />Nuovo Ordine
          </Button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Totale Ordini',      value: stats.total_orders,              sub: 'tutti gli stati' },
            { label: 'Valore Totale',      value: formatEuro(stats.total_value),   sub: 'incluse bozze' },
            { label: 'Valore Confermato',  value: formatEuro(stats.confirmed_value), sub: 'escluse bozze' },
            { label: 'Consegnati',         value: stats.by_status?.delivered || 0, sub: 'ordini completati' },
          ].map(({ label, value, sub }) => (
            <Card key={label}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-lg font-bold mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
                <p className="text-xs text-muted-foreground">{sub}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cerca per numero, cliente..." className="pl-9 h-9" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="h-9 min-w-40 text-sm" data-testid="order-status-filter">
            <SelectValue placeholder="Tutti gli stati" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <ShoppingCart size={48} className="text-muted-foreground mx-auto mb-3" />
          <h3 className="font-semibold text-lg mb-1">Nessun ordine</h3>
          {canCreate('orders') && (
            <Button className="mt-3" onClick={() => setShowForm(true)}><Plus size={14} className="mr-2" />Crea Ordine</Button>
          )}
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">N° Ordine</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Cliente</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Commerciale</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">Data</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">Consegna</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Totale</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Stato</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(o => {
                  const id = o._id || o.id;
                  const cfg = STATUS_CONFIG[o.status] || STATUS_CONFIG.draft;
                  return (
                    <tr key={id} className="border-b hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => navigate(`/orders/${id}`)}>
                      <td className="px-4 py-3">
                        <span className="font-mono font-semibold text-primary text-sm">{o.order_number}</span>
                      </td>
                      <td className="px-4 py-3 font-medium">{o.company_name || '-'}</td>
                      <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                        <UserBadge userId={o.assigned_to} size="xs" />
                      </td>
                      <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">
                        {o.order_date ? new Date(o.order_date).toLocaleDateString('it-IT') : '-'}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">
                        {o.expected_delivery ? new Date(o.expected_delivery).toLocaleDateString('it-IT') : '-'}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">{formatEuro(o.total)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>
                      </td>
                      <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate(`/orders/${id}`)}>
                            <ExternalLink size={13} />
                          </Button>
                          {canDelete('orders') && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(id, o.order_number)}>
                              <Trash2 size={13} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Order Dialog */}
      <Dialog open={showForm} onOpenChange={v => { setShowForm(v); if (!v) { setForm(initForm()); setItems([emptyItem()]); setShowDDTFields(false); setEditId(null); setEditOrderNum(''); if (searchParams.get('edit')) navigate('/orders'); } }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editId ? `Modifica Ordine ${editOrderNum}` : 'Nuovo Ordine'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-5 mt-2">

            {/* Customer row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Cliente *</Label>
                <Select value={form.company_id} onValueChange={v => setForm(f => ({ ...f, company_id: v, contact_id: '' }))}>
                  <SelectTrigger data-testid="company-select"><SelectValue placeholder="Seleziona cliente..." /></SelectTrigger>
                  <SelectContent>{companies.map(c => <SelectItem key={c._id || c.id} value={c._id || c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Referente</Label>
                <Select value={form.contact_id} onValueChange={v => setForm(f => ({ ...f, contact_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>{contacts.map(c => <SelectItem key={c._id || c.id} value={c._id || c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Commerciale</Label>
                <Select value={form.assigned_to} onValueChange={v => setForm(f => ({ ...f, assigned_to: v }))}>
                  <SelectTrigger><SelectValue placeholder="Assegna a..." /></SelectTrigger>
                  <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Dates + ref */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1.5">
                <Label>Data Consegna Prevista</Label>
                <Input type="date" value={form.expected_delivery} onChange={e => setForm(f => ({ ...f, expected_delivery: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>Riferimento</Label>
                <Input value={form.riferimento} onChange={e => setForm(f => ({ ...f, riferimento: e.target.value }))} placeholder="Rif. ordine cliente..." />
              </div>
              <div className="space-y-1.5">
                <Label>Cod. Cliente</Label>
                <Input value={form.cod_cliente} onChange={e => setForm(f => ({ ...f, cod_cliente: e.target.value }))} placeholder="Codice cliente..." />
              </div>
            </div>

            {/* Items table */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Articoli</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus size={13} className="mr-1.5" />Aggiungi riga
                </Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" style={{ minWidth: '1320px' }}>
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="w-8 px-1" />
                        <th className="text-left px-2 py-2 font-medium text-muted-foreground w-28">Codice</th>
                        <th className="text-left px-2 py-2 font-medium text-muted-foreground min-w-44">Descrizione *</th>
                        <th className="text-left px-2 py-2 font-medium text-muted-foreground w-20">Lotto</th>
                        <th className="text-left px-2 py-2 font-medium text-muted-foreground w-12">U.M.</th>
                        <th className="text-center px-2 py-2 font-medium text-muted-foreground w-16" title="Quantità per collo (dal prodotto)">Qtà/collo</th>
                        <th className="text-center px-2 py-2 font-medium text-muted-foreground w-16">N° Colli</th>
                        <th className="text-left px-2 py-2 font-medium text-muted-foreground w-32" title="Tipo imballaggio (catalogo)">Imballaggio</th>
                        <th className="text-center px-2 py-2 font-medium text-muted-foreground w-14 text-xs">Qtà tot.</th>
                        <th className="text-right px-2 py-2 font-medium text-muted-foreground w-20 text-xs">Peso (g)</th>
                        <th className="text-right px-2 py-2 font-medium text-muted-foreground w-20 text-xs">Vol. (m³)</th>
                        <th className="text-right px-2 py-2 font-medium text-muted-foreground w-24">Prezzo €/u</th>
                        <th className="text-right px-2 py-2 font-medium text-muted-foreground w-14">Sc%</th>
                        <th className="text-right px-2 py-2 font-medium text-muted-foreground w-16">IVA</th>
                        <th className="text-right px-2 py-2 font-medium text-muted-foreground w-24">Totale</th>
                        <th className="w-8" />
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, idx) => {
                        const qty     = it.quantity;
                        const net     = qty * it.unit_price * (1 - it.discount / 100);
                        const rowTotal = net * (1 + it.vat_rate / 100);
                        const rpeso   = itemPeso(it);
                        const rvol    = itemVol(it);
                        return (
                          <tr key={it._rid || idx} className="border-t border-border">
                            <td className="px-1 py-1.5">
                              <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => openProductPicker(idx)} title="Seleziona dal catalogo">
                                <Package size={13} />
                              </Button>
                            </td>
                            <td className="px-2 py-1.5">
                              <Input value={it.product_code} onChange={e => changeItem(idx, 'product_code', e.target.value)} placeholder="Codice" className="h-8 text-xs" data-testid={`item-code-${idx}`} />
                            </td>
                            <td className="px-2 py-1.5">
                              <Input value={it.description} onChange={e => changeItem(idx, 'description', e.target.value)} placeholder="Descrizione..." className="h-8 text-sm" data-testid={`item-desc-${idx}`} />
                            </td>
                            <td className="px-2 py-1.5">
                              <Input value={it.lot_number} onChange={e => changeItem(idx, 'lot_number', e.target.value)} placeholder="Lotto" className="h-8 text-xs" />
                            </td>
                            <td className="px-2 py-1.5">
                              <Input value={it.unit_measure} onChange={e => changeItem(idx, 'unit_measure', e.target.value)} placeholder="NR" className="h-8 text-xs" />
                            </td>
                            {/* Qtà per collo — read-only from product */}
                            <td className="px-2 py-1.5 text-center">
                              <Input type="number" min="0" step="0.01" value={it.quantita_per_collo}
                                onChange={e => changeItem(idx, 'quantita_per_collo', parseFloat(e.target.value) || 1)}
                                className="h-8 text-xs text-center bg-muted/40 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                title="Quantità per collo (dal prodotto)" data-testid={`item-qtapercollo-${idx}`} />
                            </td>
                            {/* N° Colli — primary user input */}
                            <td className="px-2 py-1.5">
                              <Input type="number" min="0" step="1" value={it.num_colli}
                                onChange={e => changeItem(idx, 'num_colli', parseInt(e.target.value) || 0)}
                                className="h-8 text-sm text-center font-semibold text-foreground [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                data-testid={`item-colli-${idx}`} />
                            </td>
                            {/* Imballaggio — catalog dropdown (overrides volume) */}
                            <td className="px-2 py-1.5">
                              <select
                                value={it.packaging_id || ''}
                                onChange={e => setPackaging(idx, e.target.value)}
                                className="h-8 text-xs w-full border border-input rounded-md px-2 bg-background"
                                data-testid={`item-packaging-${idx}`}
                                title={it.packaging_id ? `${it.packaging_name} (${it.packaging_volume_m3?.toFixed(4)} m³ / collo)` : 'Nessun imballaggio'}
                              >
                                <option value="">— nessuno —</option>
                                {packagings.map(p => (
                                  <option key={p._id || p.id} value={p._id || p.id}>
                                    {p.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            {/* Qtà totale — read-only computed */}
                            <td className="px-2 py-1.5 text-center font-medium text-sm text-foreground">
                              {fmtQ(qty, 2)}
                            </td>
                            {/* Peso riga — computed */}
                            <td className="px-2 py-1.5 text-right text-xs text-muted-foreground">
                              {rpeso > 0 ? fmtQ(rpeso, 2) : '—'}
                            </td>
                            {/* Volume riga — computed */}
                            <td className="px-2 py-1.5 text-right text-xs text-muted-foreground">
                              {rvol > 0 ? fmtQ(rvol, 4) : '—'}
                            </td>
                            <td className="px-2 py-1.5">
                              <Input type="number" min="0" step="0.01" value={it.unit_price} onChange={e => changeItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} className="h-8 text-sm text-right text-foreground [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" data-testid={`item-price-${idx}`} />
                            </td>
                            <td className="px-2 py-1.5">
                              <Input type="number" min="0" max="100" step="0.1" value={it.discount} onChange={e => changeItem(idx, 'discount', parseFloat(e.target.value) || 0)} className="h-8 text-sm text-right text-foreground [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" data-testid={`item-discount-${idx}`} />
                            </td>
                            <td className="px-2 py-1.5">
                              <select value={it.vat_rate} onChange={e => changeItem(idx, 'vat_rate', parseFloat(e.target.value))} className="h-8 text-sm w-full border border-input rounded-md px-2 bg-background">
                                {VAT_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                              </select>
                            </td>
                            <td className="px-2 py-1.5 text-right font-medium text-sm">
                              {formatEuro(rowTotal)}
                            </td>
                            <td className="px-2 py-1.5">
                              <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeItem(idx)}>
                                <X size={12} />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="space-y-1 text-sm min-w-48">
                  <div className="flex justify-between gap-8"><span className="text-muted-foreground">Imponibile</span><span className="font-medium">{formatEuro(subtotal)}</span></div>
                  <div className="flex justify-between gap-8"><span className="text-muted-foreground">IVA</span><span className="font-medium">{formatEuro(taxAmount)}</span></div>
                  <div className="flex justify-between gap-8 border-t border-border pt-1"><span className="font-bold">Totale</span><span className="font-bold text-primary">{formatEuro(total)}</span></div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <Label>Note ordine</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Condizioni di pagamento, istruzioni speciali..." />
            </div>

            {/* DDT Transport section - collapsible */}
            <div className="border rounded-lg overflow-hidden">
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-sm font-semibold"
                onClick={() => setShowDDTFields(!showDDTFields)}
                data-testid="toggle-ddt-fields"
              >
                <span>Dati Trasporto DDT (opzionale)</span>
                {showDDTFields ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {showDDTFields && (
                <div className="p-4 space-y-4">
                  {/* Destination */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wider">Luogo di Destinazione</Label>
                      <Input value={form.destinazione_nome} onChange={e => setForm(f => ({ ...f, destinazione_nome: e.target.value }))} placeholder="Es. Ce.di. Terni" className="h-8 text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Indirizzo</Label>
                      <Input value={form.destinazione_indirizzo} onChange={e => setForm(f => ({ ...f, destinazione_indirizzo: e.target.value }))} placeholder="Via..." className="h-8 text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">CAP / Città</Label>
                      <Input value={form.destinazione_cap_citta} onChange={e => setForm(f => ({ ...f, destinazione_cap_citta: e.target.value }))} placeholder="00100 Roma" className="h-8 text-sm" />
                    </div>
                  </div>

                  {/* Transport details */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Causale Trasporto</Label>
                      <Input value={form.causale_trasporto} onChange={e => setForm(f => ({ ...f, causale_trasporto: e.target.value }))} placeholder="Vendita" className="h-8 text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Aspetto Esteriore Beni</Label>
                      <Input value={form.aspetto_beni} onChange={e => setForm(f => ({ ...f, aspetto_beni: e.target.value }))} className="h-8 text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Tipo Movimentazione</Label>
                      <Select value={form.tipo_movimentazione} onValueChange={v => setForm(f => ({ ...f, tipo_movimentazione: v }))}>
                        <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Normale (Vendita)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Normale (Vendita)</SelectItem>
                          <SelectItem value="S">S = Sostituzione</SelectItem>
                          <SelectItem value="A">A = Assaggio / Campionatura</SelectItem>
                          <SelectItem value="E">E = Esposizione</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Trasporto a cura + Carrier + dates */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Trasporto a cura di</Label>
                      <Input value={form.trasporto_a_cura_di} onChange={e => setForm(f => ({ ...f, trasporto_a_cura_di: e.target.value }))} placeholder="Mittente / Destinatario" className="h-8 text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Vettore</Label>
                      <Input value={form.vettore} onChange={e => setForm(f => ({ ...f, vettore: e.target.value }))} placeholder="Nome vettore" className="h-8 text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Data/Ora Inizio Trasporto</Label>
                      <Input type="datetime-local" value={form.data_ora_trasporto} onChange={e => setForm(f => ({ ...f, data_ora_trasporto: e.target.value }))} className="h-8 text-sm" />
                    </div>
                  </div>

                  {/* Colli + peso + volume — auto-calcolati dagli articoli */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground flex items-center gap-1">
                        Num. Colli Totale
                        <span className="text-[10px] text-primary font-medium">(auto)</span>
                      </Label>
                      <div className="h-8 px-3 flex items-center rounded-md border bg-muted/40 text-sm font-semibold text-primary" data-testid="auto-colli">
                        {autoColli}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground flex items-center gap-1">
                        Peso Lordo
                        <span className="text-[10px] text-primary font-medium">(auto)</span>
                      </Label>
                      <div className="h-8 px-3 flex items-center rounded-md border bg-muted/40 text-sm font-semibold text-primary" data-testid="auto-peso">
                        {autoPeso > 0 ? `${fmtQ(autoPeso, 2)} g` : '—'}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground flex items-center gap-1">
                        Volume
                        <span className="text-[10px] text-primary font-medium">(auto)</span>
                      </Label>
                      <div className="h-8 px-3 flex items-center rounded-md border bg-muted/40 text-sm font-semibold text-primary" data-testid="auto-volume">
                        {autoVol > 0 ? `${fmtQ(autoVol, 4)} m³` : '—'}
                      </div>
                    </div>
                  </div>

                  {/* Note DDT */}
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Note DDT</Label>
                    <Textarea value={form.note_ddt} onChange={e => setForm(f => ({ ...f, note_ddt: e.target.value }))} rows={2} placeholder="Note aggiuntive per il documento di trasporto..." />
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => { setShowForm(false); setForm(initForm()); setItems([emptyItem()]); setShowDDTFields(false); setEditId(null); setEditOrderNum(''); if (searchParams.get('edit')) navigate('/orders'); }}>Annulla</Button>
              <Button type="submit" className="flex-1" disabled={saving} data-testid="save-order-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editId ? 'Salva Modifiche' : 'Crea Ordine'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Product Picker Dialog */}
      <Dialog open={showProductPicker} onOpenChange={setShowProductPicker}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Seleziona Articolo dal Catalogo</DialogTitle>
          </DialogHeader>
          <div className="relative mt-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9 h-9" placeholder="Cerca codice, descrizione, lotto..." value={productSearch} onChange={e => setProductSearch(e.target.value)} autoFocus />
          </div>
          <div className="flex-1 overflow-y-auto mt-2 border rounded-lg">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                {products.length === 0 ? (
                  <><Package size={32} className="mx-auto mb-2" /><p>Catalogo vuoto. Aggiungi articoli nella sezione <b>Articoli</b>.</p></>
                ) : 'Nessun risultato'}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Codice</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Descrizione</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Lotto</th>
                    <th className="text-center px-3 py-2 font-medium text-muted-foreground text-xs">U.M.</th>
                    <th className="text-center px-3 py-2 font-medium text-muted-foreground text-xs">Qtà/Collo</th>
                    <th className="text-center px-3 py-2 font-medium text-muted-foreground text-xs">Colli</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Imballaggio</th>
                    <th className="text-right px-3 py-2 font-medium text-muted-foreground text-xs">IVA</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map(prod => {
                    const pid = prod._id || prod.id;
                    return (
                      <tr key={pid} className="border-t hover:bg-primary/5 cursor-pointer transition-colors" onClick={() => selectProduct(prod)} data-testid={`pick-product-${pid}`}>
                        <td className="px-3 py-2 font-mono text-xs font-semibold text-primary">{prod.product_code || '-'}</td>
                        <td className="px-3 py-2 font-medium">{prod.description}</td>
                        <td className="px-3 py-2 text-muted-foreground text-xs">{prod.lot_number || '-'}</td>
                        <td className="px-3 py-2 text-center text-xs">{prod.unit_measure || 'NR'}</td>
                        <td className="px-3 py-2 text-center">{prod.quantita_per_collo ?? prod.quantity_default ?? 1}</td>
                        <td className="px-3 py-2 text-center">{prod.colli_default ?? 1}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{prod.packaging_name || '—'}</td>
                        <td className="px-3 py-2 text-right"><span className="text-xs px-1.5 py-0.5 rounded bg-muted">{prod.vat_rate ?? 4}%</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">Clicca su una riga per selezionare l'articolo</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
